'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import {
    Button,
    ButtonTypes,
    ButtonVariants,
    FormField,
    FormInput,
    InlineNotification,
    NotificationVariant,
    Text,
    TextTypes,
} from '../../../MyStichtingOpenComponents/src/static'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { useTranslations } from '@/helpers/useTranslations'
import { UseResetPasswordHook } from './hooks/UseResetPasswordHook'
import { LinkToLoginPage } from '@/components/forms/LinkToLoginPage'
import { AuthFlowContentSpacing } from '@/components/layouts/auth-flow-content'
import { ReactElement } from 'react'

interface Props {
    email: string
}

export const ResetPasswordForm = (): ReactElement => {
    const translate = useTranslations()

    const { isLoading, resetPasswordFormHandler, hasError, isSuccessful, isButtonDisabled } = UseResetPasswordHook()

    const ERROR_MSG: { [key: string]: string } = {
        REQUIRED: translate('MAIL_REQUIRED_VALIDATION'),
        E_MAIL: translate('MAIL_REQUIRED_VALIDATION'),
    }

    const validationSchema = yup.object().shape({
        email: yup.string().email(ERROR_MSG.E_MAIL).required(ERROR_MSG.REQUIRED),
    })

    const {
        control,
        trigger,
        handleSubmit,
        formState: { isValid, errors },
    } = useForm({
        shouldUseNativeValidation: false,
        mode: 'onBlur',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            email: '',
        },
    })

    /** stops submit and shows error messages if form is invalid */
    const validate = (): void => {
        if (!isValid) {
            trigger()
            return
        }
    }
    const onSubmit = async (data: Props) => {
        validate()
        await resetPasswordFormHandler(data)
    }

    return (
        <form>
            <AuthFlowContentSpacing>
                <Text type={TextTypes.HEADING_1} className="mb-8">
                    {translate('FORGOT_PASSWORD_TITLE')}
                </Text>
                <FormField
                    name="email"
                    label={translate('MAIL_ADDRESS_LABEL')}
                    helpText={translate('MAIL_HELP_TEXT')}
                    error={errors?.email?.message || undefined}
                >
                    <Controller
                        control={control}
                        name="email"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <FormInput
                                value={value}
                                name="email"
                                onChange={(value: string) => onChange({ target: { value } })}
                                onBlur={() => new Promise<void>((resolve) => resolve(onBlur()))}
                                error={!!errors?.email?.message}
                            />
                        )}
                    />
                </FormField>
                {isSuccessful && !hasError && (
                    <>
                        <InlineNotification
                            className="mt-6 mb-6"
                            id="success"
                            message={translate('MAIL_SUCCESS')}
                            variant={NotificationVariant.SUCCESS}
                            dismissable={false}
                        />

                        <InlineNotification
                            className="mt-6 mb-6"
                            id="info"
                            message={translate('MAIL_INFO')}
                            variant={NotificationVariant.INFO}
                            dismissable={false}
                        />
                    </>
                )}
                {hasError && (
                    <InlineNotification
                        className="mt-6"
                        id="info"
                        message={translate('GENERAL_ERROR_MESSAGE')}
                        variant={NotificationVariant.ERROR}
                        dismissable={false}
                    />
                )}
                <Button
                    loading={isLoading}
                    type={ButtonTypes.SUBMIT}
                    variant={ButtonVariants.PRIMARY}
                    className="mt-6"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isButtonDisabled}
                >
                    {isSuccessful ? translate('SET_PASSWORD_SEND_LINK_AGAIN') : translate('SET_PASSWORD_SEND_LINK')}
                </Button>
                <LinkToLoginPage className="mt-6" />
            </AuthFlowContentSpacing>
        </form>
    )
}
